import { beginLoading, endLoading } from '@app/AppLayout/LoadingContext';
import { useNotification } from '@app/AppLayout/NotificationContext';
import { DataLoadError } from '@app/components/DataLoadError';
import { ClassModalityLabel, ClassTypeLabel, InstructorNames, RequestStatusLabel, formatStatusText } from '@app/components/KlassLabels';
import { PageActionsMenu, PageActionsMenuItem } from '@app/components/PageActionsMenu';
import { useEntity } from '@app/hooks/useEntity';
import { useRowRestore } from '@app/hooks/useRowRestore';
import { useTablePreferences } from '@app/hooks/useTablePreferences';
import { KLASS_TYPE, REQUEST_STATUS } from '@app/model/Enums';
import { instructorNames } from '@app/model/Klass';
import { PipelineKlass, exportPipelineKlassData } from '@app/model/PipelineKlass';
import { usePipelineKlassStore } from '@app/store/entityStore';
import { ExportFormat, exportRecords } from '@app/utils/export';
import { ddMMyyyyFormat, ddMMyyyyHHmmFormat } from '@app/utils/functions';
import {
  ActionList, ActionListItem, AlertVariant, Badge, Bullseye, Button, EmptyState,
  EmptyStateActions, EmptyStateBody, EmptyStateFooter, Icon, MenuToggle, MenuToggleElement,
  PageSection, Pagination, PaginationVariant, SearchInput, Select, SelectOption, Split, SplitItem,
  Title, Toolbar, ToolbarContent, ToolbarFilter, ToolbarGroup, ToolbarItem, ToolbarLabel,
  ToolbarLabelGroup, ToolbarToggleGroup, Tooltip
} from '@patternfly/react-core';
import { ExclamationTriangleIcon, FilterIcon, PencilAltIcon, SearchIcon } from '@patternfly/react-icons';
import { InnerScrollContainer, OuterScrollContainer, Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Pipeline requests: what has been asked for before a class exists.
 *
 * Most rows arrive from the system that raises them; New Request is for the one that did not, and
 * opens the same detail page - which is where a request is worked on either way. Nothing is deleted
 * from here: a request the import still knows about comes straight back on the next message
 * carrying its request id.
 *
 * Built on the Classes overview's shape rather than the reference-data pages': the same session-kept
 * filters, the same frozen first column, the same export of the whole filtered view.
 */

/** The empty filter set: an entry per filterable field, none of them selected. */
const emptyFilters = (): Record<string, string[]> => ({
  requestId: [],
  customerName: [],
  course: [],
  requestStatus: [],
  type: []
});

/**
 * What the user has set the page up to show, kept for the whole session so leaving and coming back
 * lands on the view they left. Module memory rather than sessionStorage, for the reason the Classes
 * overview keeps its own the same way: a reload and a logout should clear it, and both do for free.
 */
const view = {
  filters: emptyFilters(),
  page: 1,
  selectedRowId: null as number | null
};

/** Filter label as shown -> the key it filters on. */
const CATEGORY_MAP: Record<string, string> = {
  'Request Id': 'requestId',
  'Customer': 'customerName',
  'Course Name/SKU': 'course',
  'Request Status': 'requestStatus',
  'Type': 'type'
};

const columns = [
  'Request Id', 'Created', 'Status', 'Type', 'Customer', 'SKU', 'Delivery Date', 'Alternatives',
  'Modality', 'Language', 'Instructors', 'Location', 'TSS', 'Client Executive'
];

const PipelineOverview: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const { addNotification } = useNotification();

  const { items: rows, error, retry } = useEntity(usePipelineKlassStore);

  const [filters, setFilters] = React.useState<Record<string, string[]>>(view.filters);
  const [page, setPage] = React.useState<number>(view.page);
  const [selectedRowId, setSelectedRowId] = React.useState<number | null>(view.selectedRowId);
  const { perPage, setPerPage } = useTablePreferences('pipeline', 20);
  const { rememberRow, rowProps } = useRowRestore('pipeline');

  const [currentCategory, setCurrentCategory] = React.useState<string>('Request Id');
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = React.useState(false);
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState('');

  const [activeSortIndex, setActiveSortIndex] = React.useState<number | null>(null);
  const [activeSortDirection, setActiveSortDirection] = React.useState<'asc' | 'desc' | null>(null);

  React.useEffect(() => { view.filters = filters; }, [filters]);
  React.useEffect(() => { view.page = page; }, [page]);
  React.useEffect(() => { view.selectedRowId = selectedRowId; }, [selectedRowId]);

  const activeFilterCount = React.useMemo(
    () => Object.values(filters).reduce((total, arr) => total + (arr?.length || 0), 0),
    [filters]
  );

  // --- Filter handlers ---
  const applyFilters = (newFilters: Record<string, string[]>) => {
    setFilters(newFilters);
    setPage(1);
  };

  const onTextSearch = (value: string) => {
    if (!value.trim()) return;
    const field = CATEGORY_MAP[currentCategory];
    if (!field) return;

    const previous = filters[field] || [];
    if (!previous.includes(value.trim())) {
      applyFilters({ ...filters, [field]: [...previous, value.trim()] });
      setInputValue('');
    }
  };

  const onDeleteFilter = (category: string | ToolbarLabelGroup, id: string | ToolbarLabel) => {
    const stateKey = CATEGORY_MAP[String(category)];
    if (stateKey && filters[stateKey]) {
      applyFilters({ ...filters, [stateKey]: filters[stateKey].filter(f => f !== id) });
    }
  };

  const clearFilters = () => {
    applyFilters(emptyFilters());
    setInputValue('');
  };

  /** Adds or removes one value of a multi-select filter. */
  const onCheckboxSelect = (stateKey: string) =>
    (event?: React.MouseEvent<Element, MouseEvent>, selection?: string | number) => {
      if (!event) return;
      const checked = (event.target as HTMLInputElement).checked;
      const current = filters[stateKey] || [];
      applyFilters({
        ...filters,
        [stateKey]: checked ? [...current, String(selection)] : current.filter(v => v !== String(selection))
      });
    };

  // --- Derivations ---
  const filteredRows = React.useMemo(() => rows.filter((row) => {
    const matchesRequestId = filters.requestId?.length === 0
      || filters.requestId?.some(r => row.requestId?.toLowerCase().includes(r.toLowerCase()));

    const matchesCustomer = filters.customerName?.length === 0
      || filters.customerName?.some(c => row.customerName?.toLowerCase().includes(c.toLowerCase()));

    const matchesCourse = filters.course?.length === 0 || filters.course?.some(c => {
      const term = c.toLowerCase();
      return row.course?.name?.toLowerCase().includes(term) || row.course?.sku?.toLowerCase().includes(term);
    });

    const matchesStatus = filters.requestStatus?.length === 0
      || filters.requestStatus?.includes(row.requestStatus as string);

    const matchesType = filters.type?.length === 0 || filters.type?.includes(row.type as string);

    return matchesRequestId && matchesCustomer && matchesCourse && matchesStatus && matchesType;
  }), [rows, filters]);

  // A page carried over from earlier in the session can point past the end of the results once the
  // requests themselves have moved on. Fall back to the last page holding rows rather than showing
  // "no results" over a list that plainly has some.
  React.useEffect(() => {
    const lastPage = Math.ceil(filteredRows.length / perPage);
    if (lastPage > 0 && page > lastPage) setPage(lastPage);
  }, [filteredRows.length, perPage, page]);

  const sortedRows = React.useMemo(() => {
    if (activeSortIndex === null || activeSortDirection === null) return filteredRows;

    const keyOf = (row: PipelineKlass): string | number => {
      switch (activeSortIndex) {
        case 0: return row.requestId?.toLowerCase() || '';
        case 1: return row.creationDate ? new Date(row.creationDate).getTime() : 0;
        case 2: return row.requestStatus || '';
        case 3: return row.type || '';
        case 4: return row.customerName?.toLowerCase() || '';
        case 5: return row.course?.sku?.toLowerCase() || '';
        case 6: return row.deliveryDate ? new Date(row.deliveryDate).getTime() : 0;
        case 8: return row.modality || '';
        case 9: return row.language?.englishName?.toLowerCase() || '';
        case 10: return instructorNames(row.instructors).toLowerCase();
        case 11: return [row.city, row.country?.name].filter(Boolean).join(', ').toLowerCase();
        case 12: return row.tss?.name?.toLowerCase() || '';
        case 13: return row.clientExecutive?.name?.toLowerCase() || '';
        default: return '';
      }
    };

    return [...filteredRows].sort((a, b) => {
      const aValue = keyOf(a);
      const bValue = keyOf(b);
      if (aValue < bValue) return activeSortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return activeSortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredRows, activeSortIndex, activeSortDirection]);

  const paginatedRows = React.useMemo(() => {
    const startIdx = (page - 1) * perPage;
    return sortedRows.slice(startIdx, startIdx + perPage);
  }, [sortedRows, page, perPage]);

  // --- Export ---
  // The whole active view, not just the page on screen: every request left by the filters, in the
  // order the table is currently sorted.
  const exportPipeline = (exportFormat: ExportFormat) => {
    if (sortedRows.length === 0) {
      addNotification('Nothing to export', AlertVariant.warning, 'No request matches the current filters.');
      return;
    }
    beginLoading();
    try {
      const fileName = exportRecords(sortedRows.map(exportPipelineKlassData), 'pipeline', exportFormat, 'Pipeline');
      addNotification('Export ready', AlertVariant.success,
        `${sortedRows.length} request${sortedRows.length === 1 ? '' : 's'} exported to ${fileName}`);
    } catch (err: any) {
      addNotification('Error exporting requests', AlertVariant.danger, err.message);
    } finally {
      endLoading();
    }
  };

  const exportMenuItems: PageActionsMenuItem[] = [
    { key: 'export-csv', label: 'Export view to CSV...', onClick: () => exportPipeline('csv'), isDisabled: sortedRows.length === 0 },
    { key: 'export-xlsx', label: 'Export view to Excel...', onClick: () => exportPipeline('xlsx'), isDisabled: sortedRows.length === 0 }
  ];

  // --- Render helpers ---
  const getSortParams = (columnIndex: number) => ({
    sortBy: {
      index: activeSortIndex !== null ? activeSortIndex : undefined,
      direction: activeSortDirection !== null ? activeSortDirection : undefined,
      defaultDirection: 'asc' as const
    },
    onSort: (_event: React.MouseEvent, index: number, direction: 'asc' | 'desc') => {
      setActiveSortIndex(index);
      setActiveSortDirection(direction);
    },
    columnIndex
  });

  const buildPagination = (variant: 'bottom' | 'top', isCompact: boolean) => (
    <Pagination isCompact={isCompact} itemCount={filteredRows.length} page={page} perPage={perPage}
      onSetPage={(_evt, newPage) => setPage(newPage)}
      onPerPageSelect={(_evt, newPerPage, newPage) => { setPerPage(newPerPage); setPage(newPage); }}
      variant={variant as PaginationVariant} isStatic
    />
  );

  const buildCategoryDropdown = () => (
    <ToolbarItem>
      <Select
        onSelect={(_e, val) => { setCurrentCategory(val as string); setIsCategoryDropdownOpen(false); setInputValue(''); }}
        selected={currentCategory} isOpen={isCategoryDropdownOpen} onOpenChange={setIsCategoryDropdownOpen}
        toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
          <MenuToggle ref={toggleRef} onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
            isExpanded={isCategoryDropdownOpen} icon={<FilterIcon />} style={{ width: '100%', verticalAlign: 'text-bottom' }}>
            {currentCategory}
          </MenuToggle>
        )}
      >
        {Object.keys(CATEGORY_MAP).map(category => (
          <SelectOption key={category} value={category}>{category}</SelectOption>
        ))}
      </Select>
    </ToolbarItem>
  );

  const renderTextFilter = (categoryName: string, stateKey: string) => (
    <ToolbarFilter
      key={categoryName}
      labels={filters[stateKey] || []}
      deleteLabel={onDeleteFilter}
      categoryName={categoryName}
      showToolbarItem={currentCategory === categoryName}
    >
      <SearchInput
        aria-label={`${categoryName} filter`}
        placeholder={`Filter by ${categoryName}...`}
        onChange={(_e, val) => setInputValue(val)}
        value={inputValue}
        onClear={() => setInputValue('')}
        onSearch={(_e, value) => onTextSearch(value)}
      />
    </ToolbarFilter>
  );

  /** A checkbox dropdown over the values of an enum. */
  const renderEnumFilter = (categoryName: string, stateKey: string, values: string[]) => (
    <ToolbarFilter labels={filters[stateKey]} deleteLabel={onDeleteFilter} categoryName={categoryName}
      showToolbarItem={currentCategory === categoryName}>
      <Select aria-label={categoryName} isOpen={isFilterDropdownOpen} onOpenChange={setIsFilterDropdownOpen}
        popperProps={{ minWidth: '180px' }} onSelect={onCheckboxSelect(stateKey)} selected={filters[stateKey]}
        toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
          <MenuToggle ref={toggleRef} onClick={() => setIsFilterDropdownOpen(!isFilterDropdownOpen)}
            isExpanded={isFilterDropdownOpen} style={{ width: '100%', verticalAlign: 'text-bottom' }}>
            Filter by {categoryName.toLowerCase()}
            {filters[stateKey] && filters[stateKey].length > 0 && <Badge isRead>{filters[stateKey].length}</Badge>}
          </MenuToggle>
        )}>
        {values.map(value => (
          <SelectOption hasCheckbox key={value} value={value} isSelected={filters[stateKey]?.includes(value)}>
            {formatStatusText(value)}
          </SelectOption>
        ))}
      </Select>
    </ToolbarFilter>
  );

  /** Where the request asks to be delivered. A request names a city, never a facility. */
  const locationOf = (row: PipelineKlass) => [row.city, row.country?.name].filter(Boolean).join(', ') || 'N/A';

  const tableViewportHeight = 'calc(100vh - var(--cas-table-offset))';

  return (
    <React.Fragment>
      <PageSection hasBodyWrapper isWidthLimited>
        <Split>
          <SplitItem>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Title headingLevel="h1" size="4xl">Pipeline</Title>
              {activeFilterCount > 0 && (
                <Badge isRead>{activeFilterCount} Active Filter{activeFilterCount !== 1 ? 's' : ''}</Badge>
              )}
            </div>
          </SplitItem>
          <SplitItem isFilled />
          <SplitItem>
            <ActionList>
              <ActionListItem>
                <Button variant="primary" id="new-button" onClick={() => navigate('/classes/pipeline/new')}>
                  New Request
                </Button>
              </ActionListItem>
              <ActionListItem>
                <PageActionsMenu onRefresh={retry} items={exportMenuItems} />
              </ActionListItem>
            </ActionList>
          </SplitItem>
        </Split>
      </PageSection>

      <Toolbar id="pipeline-toolbar" clearAllFilters={clearFilters} collapseListedFiltersBreakpoint="xl">
        <ToolbarContent>
          <ToolbarToggleGroup toggleIcon={<FilterIcon />} breakpoint="xl">
            <ToolbarGroup variant="filter-group" style={{ lineHeight: '22px', alignItems: 'center' }}>
              {buildCategoryDropdown()}
              {renderTextFilter('Request Id', 'requestId')}
              {renderTextFilter('Customer', 'customerName')}
              {renderTextFilter('Course Name/SKU', 'course')}
              {renderEnumFilter('Request Status', 'requestStatus', Object.values(REQUEST_STATUS))}
              {renderEnumFilter('Type', 'type', Object.values(KLASS_TYPE))}
            </ToolbarGroup>
          </ToolbarToggleGroup>
          <ToolbarItem variant="pagination">{buildPagination('top', true)}</ToolbarItem>
        </ToolbarContent>
      </Toolbar>

      {paginatedRows.length === 0 ? (
        /* Nothing to draw: stand in for the table and fill the space it would have taken, so the
           message lands in the middle of the viewport. Distinguishes "the load failed" from
           "no requests match". */
        <div style={{ height: tableViewportHeight, overflow: 'auto' }}>
          {error ? (
            <DataLoadError error={error} entityLabel="pipeline requests" onRetry={retry} />
          ) : (
            <Bullseye>
              <EmptyState headingLevel="h5" icon={SearchIcon} titleText="No requests found">
                <EmptyStateBody>
                  {activeFilterCount > 0
                    ? 'No pipeline request matches the filter criteria. Clear all filters and try again.'
                    : 'There are no pipeline requests. They arrive from the system that raises them, or can be added here.'}
                </EmptyStateBody>
                <EmptyStateFooter>
                  <EmptyStateActions>
                    {activeFilterCount > 0
                      ? <Button variant="link" onClick={clearFilters}>Clear all filters</Button>
                      : <Button variant="primary" onClick={() => navigate('/classes/pipeline/new')}>New Request</Button>}
                  </EmptyStateActions>
                </EmptyStateFooter>
              </EmptyState>
            </Bullseye>
          )}
        </div>
      ) : (
        <OuterScrollContainer style={{ maxHeight: tableViewportHeight, minHeight: 0 }}>
          <InnerScrollContainer>
            {/* gridBreakPoint="" keeps the real table layout at every width, so the frozen column
                and the horizontal scroll never fall back to stacked cards. */}
            <Table aria-label="Pipeline requests" isStickyHeader variant='compact' gridBreakPoint=""
              className="cas-wide-table">
              <Thead>
                <Tr>
                  <Th sort={getSortParams(0)} modifier="truncate" isStickyColumn stickyMinWidth="120px"
                    stickyLeftOffset="0" hasRightBorder>{columns[0]}</Th>
                  <Th sort={getSortParams(1)}>{columns[1]}</Th>
                  <Th sort={getSortParams(2)}>{columns[2]}</Th>
                  <Th sort={getSortParams(3)}>{columns[3]}</Th>
                  <Th sort={getSortParams(4)}>{columns[4]}</Th>
                  <Th sort={getSortParams(5)}>{columns[5]}</Th>
                  <Th sort={getSortParams(6)}>{columns[6]}</Th>
                  <Th>{columns[7]}</Th>
                  <Th sort={getSortParams(8)}>{columns[8]}</Th>
                  <Th sort={getSortParams(9)}>{columns[9]}</Th>
                  <Th sort={getSortParams(10)}>{columns[10]}</Th>
                  <Th sort={getSortParams(11)}>{columns[11]}</Th>
                  <Th sort={getSortParams(12)}>{columns[12]}</Th>
                  <Th sort={getSortParams(13)}>{columns[13]}</Th>
                  <Th className="cas-col-tight cas-col-actions" isStickyColumn stickyMinWidth="84px"
                    stickyRightOffset="0">Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {paginatedRows.map((row) => (
                  <Tr key={row.id} {...rowProps(row.id)}
                    isSelectable isClickable
                    isRowSelected={selectedRowId === row.id}
                    onRowClick={() => setSelectedRowId(row.id)}>
                    <Td dataLabel={columns[0]} modifier="truncate" isStickyColumn stickyMinWidth="120px"
                      stickyLeftOffset="0" hasRightBorder>
                      <b>{row.requestId}</b>
                    </Td>
                    <Td dataLabel={columns[1]}>{ddMMyyyyHHmmFormat(row.creationDate) || 'N/A'}</Td>
                    <Td dataLabel={columns[2]} textCenter><RequestStatusLabel status={row.requestStatus} /></Td>
                    <Td dataLabel={columns[3]} textCenter><ClassTypeLabel type={row.type} /></Td>
                    <Td dataLabel={columns[4]}>{row.customerName || 'N/A'}</Td>
                    <Td dataLabel={columns[5]} textCenter>
                      {/* A SKU that resolved to nothing leaves the course unset, and the request
                          visibly incomplete: it is importable but needs the course set by hand. */}
                      {row.course
                        ? <Tooltip content={row.course.name}><span className="tooltipped-text-bold">{row.course.sku}</span></Tooltip>
                        : (
                          <Tooltip content="The SKU on this request did not match a course. Set it on the detail page.">
                            <Icon status="warning"><ExclamationTriangleIcon /></Icon>
                          </Tooltip>
                        )}
                    </Td>
                    <Td dataLabel={columns[6]}>{ddMMyyyyFormat(row.deliveryDate) || 'N/A'}</Td>
                    <Td dataLabel={columns[7]} modifier="nowrap">
                      {[row.alternativeDate, row.alternativeDate2].map(ddMMyyyyFormat).filter(Boolean).join(', ') || 'N/A'}
                    </Td>
                    <Td dataLabel={columns[8]} textCenter><ClassModalityLabel modality={row.modality} /></Td>
                    <Td dataLabel={columns[9]}>{row.language?.englishName || 'N/A'}</Td>
                    <Td dataLabel={columns[10]}><InstructorNames instructors={row.instructors} course={row.course} /></Td>
                    <Td dataLabel={columns[11]}>{locationOf(row)}</Td>
                    <Td dataLabel={columns[12]} modifier="nowrap">{row.tss?.name || 'N/A'}</Td>
                    <Td dataLabel={columns[13]} modifier="nowrap">{row.clientExecutive?.name || 'N/A'}</Td>
                    <Td dataLabel="Actions" modifier="nowrap" className="cas-col-tight cas-col-actions" textCenter
                      isStickyColumn stickyMinWidth="84px" stickyRightOffset="0">
                      <Tooltip content="Edit">
                        <Button
                          icon={<Icon size="lg"><PencilAltIcon /></Icon>}
                          variant="plain"
                          className="compactButtonStyle"
                          onClick={() => {
                            rememberRow(row.id, page);
                            navigate(`/classes/pipeline/${row.id}`);
                          }}
                        />
                      </Tooltip>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </InnerScrollContainer>
        </OuterScrollContainer>
      )}
      {buildPagination('bottom', false)}
    </React.Fragment>
  );
};

export { PipelineOverview };
